import React, { useState } from 'react';
import { Ticket } from '../../types/event';
import { motion } from 'framer-motion';
import { TicketIcon as SolidTicketIcon, StarIcon, XCircleIcon, BanknotesIcon, SparklesIcon, GiftIcon } from '@heroicons/react/24/solid'; // Using solid icons for more impact
import { ChevronDown, ChevronUp } from 'lucide-react';

interface TicketListProps {
  tickets: Ticket[];
  loading: boolean;
  onSelectTicket: (ticket: Ticket) => void;
  isEventPast?: boolean;
}

const getTicketTypeDetails = (type: string) => {
  const typeName = type.toLowerCase() === 'vip' ? 'VIP' : 
                 type.toLowerCase() === 'free' ? 'GRATUIT' :
                 type.toLowerCase() === 'early_bird' ? 'EARLY BIRD' : 'STANDARD';
                 
  switch (type.toLowerCase()) {
    case 'vip':
      return {
        icon: <StarIcon className="w-7 h-7 text-yellow-300" />,
        gradient: 'from-yellow-500/20 via-black to-yellow-700/20 border-yellow-400/60',
        textColor: 'text-yellow-200',
        buttonGradient: 'bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700',
        ribbonText: typeName,
        ribbonColor: 'from-yellow-300 to-yellow-500 text-black',
        nameColor: 'text-yellow-100',
        borderAccent: 'border-l-4 border-l-yellow-400'
      };
    case 'free':
      return {
        icon: <GiftIcon className="w-7 h-7 text-purple-300" />,
        gradient: 'from-purple-500/20 via-black to-purple-700/20 border-purple-400/60',
        textColor: 'text-purple-200',
        buttonGradient: 'bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700',
        ribbonText: typeName,
        ribbonColor: 'from-purple-300 to-purple-500 text-white',
        nameColor: 'text-purple-100',
        borderAccent: 'border-l-4 border-l-purple-400'
      };
    case 'early_bird':
      return {
        icon: <SparklesIcon className="w-7 h-7 text-pink-300" />,
        gradient: 'from-pink-500/20 via-black to-pink-700/20 border-pink-400/60',
        textColor: 'text-pink-200',
        buttonGradient: 'bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700',
        ribbonText: typeName,
        ribbonColor: 'from-pink-300 to-pink-500 text-white',
        nameColor: 'text-pink-100',
        borderAccent: 'border-l-4 border-l-pink-400'
      };
    case 'standard':
    default:
      return {
        icon: <SolidTicketIcon className="w-7 h-7 text-cyan-300" />,
        gradient: 'from-cyan-500/20 via-black to-cyan-700/20 border-cyan-400/60',
        textColor: 'text-cyan-200',
        buttonGradient: 'bg-gradient-to-r from-cyan-400 to-cyan-600 hover:from-cyan-500 hover:to-cyan-700',
        ribbonText: typeName,
        ribbonColor: 'from-cyan-300 to-cyan-500 text-black',
        nameColor: 'text-cyan-100',
        borderAccent: 'border-l-4 border-l-cyan-400'
      };
  }
};

const TicketList: React.FC<TicketListProps> = ({ 
  tickets, 
  loading, 
  onSelectTicket,
  isEventPast = false
}) => {
  const [showAllBenefits, setShowAllBenefits] = useState<Record<string, boolean>>({});

  const toggleBenefits = (ticketId: string) => {
    setShowAllBenefits(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[1, 2].map((i) => (
          <div key={i} className="bg-neutral-800/50 rounded-2xl p-6 border border-neutral-700/50 animate-pulse h-80 shadow-2xl"></div>
        ))}
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="bg-neutral-800/30 rounded-2xl p-10 text-center border border-neutral-700/30 backdrop-blur-sm shadow-xl">
        <XCircleIcon className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
        <p className="text-xl font-semibold text-neutral-300">Aucun ticket disponible</p>
        <p className="text-neutral-400 mt-2">Veuillez vérifier ultérieurement ou contacter l'organisateur.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap justify-center gap-6">
      {tickets.map((ticket) => {
        const details = getTicketTypeDetails(ticket.ticket_type);
        const isSoldOut = ticket.available_quantity !== undefined && ticket.available_quantity <= 0;
        const isDisabled = isSoldOut || isEventPast;

        return (
          <motion.div
            key={ticket.id}
            className={`w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] relative group rounded-2xl overflow-hidden shadow-2xl border ${details.gradient} ${isDisabled ? 'filter grayscale contrast-75 opacity-60' : 'hover:shadow-[0_0_35px_5px_rgba(255,255,255,0.1)] hover:border-yellow-400/70 transition-all duration-300'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-60 mix-blend-overlay"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-black/20 to-transparent"></div>
            </div>
            
            {!isDisabled && (
              <div className={`absolute top-3 -right-10 w-32 bg-gradient-to-r ${details.ribbonColor} text-xs font-bold px-4 py-1.5 transform rotate-45 shadow-lg z-10 flex items-center justify-center`}>
                {details.ribbonText}
              </div>
            )}
            
            <div className={`flex flex-col h-full p-6 bg-black/50 backdrop-blur-xl relative z-0 ${details.borderAccent}`}>
              {/* Header */}
              <div className="flex items-center mb-4 pb-4 border-b border-white/10">
                {details.icon}
                <h3 className={`text-2xl font-bold ${details.nameColor} tracking-tight`}>{ticket.name}</h3>
              </div>

              {/* Description */}
              {ticket.description && (
                <p className={`text-sm ${details.textColor} opacity-80 mb-4 min-h-[40px] line-clamp-2`}>
                  {ticket.description}
                </p>
              )}
              
              {/* Benefits */}
              {ticket.benefits && (
                <div className="mt-2 mb-4 space-y-2 flex-grow">
                  {ticket.benefits
                    .split('\n')
                    .filter(benefit => benefit.trim() !== '')
                    .slice(0, showAllBenefits[ticket.id] ? undefined : 3)
                    .map((benefit, index) => (
                      <div key={index} className="flex items-center px-3 py-2 rounded-lg bg-yellow-500/10">
                        <svg className="w-5 h-5 mr-2 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className={`text-sm ${details.textColor} opacity-90`}>{benefit.trim()}</span>
                      </div>
                    ))}
                  
                  {ticket.benefits.split('\n').filter(benefit => benefit.trim() !== '').length > 3 && (
                    <button
                      onClick={() => toggleBenefits(ticket.id)}
                      className="w-full flex items-center justify-center text-xs text-yellow-400 hover:text-yellow-300 mt-2 focus:outline-none"
                    >
                      {showAllBenefits[ticket.id] ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Voir moins d'avantages
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          + {ticket.benefits.split('\n').filter(benefit => benefit.trim() !== '').length - 3} avantages supplémentaires
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
              
              {/* Price and Quantity Info */}
              <div className="mt-auto space-y-4">
                <div className='flex items-baseline justify-between'> 
                    <p className={`text-4xl font-extrabold ${details.textColor}`}>
                        {ticket.price === 0 ? 'GRATUIT' : `${ticket.price.toLocaleString('fr-FR')} FCFA`}
                    </p>
                    {ticket.ticket_type !== 'free' && ticket.original_price && ticket.original_price > ticket.price && (
                        <p className="text-sm text-neutral-400 line-through ml-2">
                            {ticket.original_price.toLocaleString('fr-FR')} FCFA
                        </p>
                    )}
                </div>
                
                {ticket.available_quantity !== null && ticket.available_quantity !== undefined && !isEventPast && (
                  <div className="flex items-center space-x-2">
                    <div className="relative w-full bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-full rounded-full ${isSoldOut ? 'bg-red-400' : details.textColor.replace('text-', 'bg-')}`}
                        style={{ width: `${isSoldOut ? 0 : 100}%` }}
                      ></div>
                    </div>
                    <span className={`text-xs font-medium ${isSoldOut ? 'text-red-400' : details.textColor}`}>
                      {isSoldOut ? 'ÉPUISÉ' : `${ticket.available_quantity} ${ticket.available_quantity > 1 ? 'restants' : 'restant'}`}
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={() => onSelectTicket(ticket)}
                  disabled={isDisabled}
                  className={`w-full py-3 px-4 rounded-lg font-bold text-white ${details.buttonGradient} transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg flex items-center justify-center gap-2`}
                >
                  {isEventPast ? 'Événement terminé' : isSoldOut ? 'Épuisé' : (
                    <>
                      <span>Obtenir le ticket</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default TicketList;
